/**
 * **추적 칸** — 플랜이 주는 칸을 «몇 개 썼나 · 몇 개까지 되나 · 막혔나 · 다 찼나»로 한 번에 센다.
 *
 * ```
 *   갈래            이름              쓴 수(출처)                                   한도
 *   brandKeyword    브랜드 키워드 칸   brandSetting.brandKeyword(대소문자 중복은 하나)  brand.brandKeywordCount
 *   brandAccount    자사 계정 칸      brandSetting.brandAccounts(채널·아이디 있는 것)  brandAccount.brandAccountCount
 *   targetKeyword   타겟 키워드 칸    살아 있는 세트들의 키워드 + 계정(중복도 각각)     keyword.keywordCount
 *   seedingAccount  시딩 계정 칸      진행 중 시딩 캠페인들의 계정                      campaign.campaignAccountCount(맞춤 플랜은 seeding.maxLimit 우선)
 * ```
 *
 * 🔑 **칸을 그리는 자리는 전부 이 파일을 쓴다** — 추적 관리 · 추적 등록 창 · 시작하기 · 구독 카드 · 시딩 목록.
 *   한 자리가 따로 세면 같은 순간에 숫자가 갈린다(7/10 과 6/10).
 * 🔑 막힘 판정은 `isPlanTrackAreaLocked`(허용값 + 한도 0) 하나다 — 시딩은 시딩 관문이 따로 막으므로 여기서 잠그지 않는다.
 * 🔑 색 규칙은 하나다 — 다 차면 `warning.main`, 아니면 `text.secondary`.
 * ⚠️ 순수 함수다 — `'use client'` 를 붙이지 않는다.
 */

import type { PlanConfig } from 'src/types/plan/plan-domain';

import { fNumber } from 'src/utils/format/format-number';
import { getCampaignAccounts } from 'src/utils/campaign/campaign-accounts';
import { filterInProgressCampaigns } from 'src/utils/campaign/campaign-period-status';
import { toPlanTrackLimit, isPlanTrackAreaLocked } from 'src/utils/plan/plan-track-gate';
import { acceptsCustomPlanService, getWorkspacePlanAccessContext } from 'src/utils/plan/plan-access';

/** 칸의 갈래 */
export type TrackArea = 'brandKeyword' | 'brandAccount' | 'targetKeyword' | 'seedingAccount';

/** 칸의 이름 — 화면마다 이 말 하나로 부른다 */
export const TRACK_AREA_NAME: Record<TrackArea, string> = {
  brandKeyword: '브랜드 키워드 칸',
  brandAccount: '자사 계정 칸',
  targetKeyword: '타겟 키워드 칸',
  seedingAccount: '시딩 계정 칸',
};

/** 시딩 계정 칸의 한도 — 워크스페이스 문서가 아직 없을 때(불러오는 중) */
export const SEEDING_ACCOUNT_DEFAULT_LIMIT = 1000;

/** 칸 하나의 형편 */
export type TrackCapacity = {
  area: TrackArea;
  /** `TRACK_AREA_NAME` 의 말 */
  name: string;
  used: number;
  /** 모르면 `null` — 그때는 쓴 수만 적는다 */
  limit: number | null;
  /** 플랜이 이 갈래를 안 준다 */
  locked: boolean;
  /** 한도까지 다 썼다(막힌 것과 다르다) */
  full: boolean;
  /** 수의 색 — 다 차면 주황 */
  color: 'warning.main' | 'text.secondary';
};

function text(value: unknown): string {
  return String(value ?? '').trim();
}

/** 브랜드 키워드 칸의 쓴 수 — 대소문자만 다른 것은 하나로 센다(추적 관리의 줄 수와 같다) */
export function countBrandKeywordUsage(brandSetting: Record<string, any> | null | undefined): number {
  const raw = Array.isArray(brandSetting?.brandKeyword) ? brandSetting.brandKeyword : [];
  return new Set(raw.map((v: unknown) => text(v).toLowerCase()).filter(Boolean)).size;
}

/** 자사 계정 칸의 쓴 수 — 채널과 아이디가 다 있는 것만 */
export function countBrandAccountUsage(brandSetting: Record<string, any> | null | undefined): number {
  const raw = Array.isArray(brandSetting?.brandAccounts) ? brandSetting.brandAccounts : [];
  return raw.filter((account: any) => {
    if (typeof account === 'string') return Boolean(text(account));
    const platform = text(account?.platform ?? account?.type);
    const username = text(account?.username ?? account?.id ?? account?.handle ?? account?.account);
    return Boolean(platform && username);
  }).length;
}

/**
 * 타겟 키워드 칸의 쓴 수 — 살아 있는 세트들이 건 키워드 + 계정의 합.
 * 🚨 세트 수가 아니다 — 한도의 단위가 「키워드 + 계정」이다.
 * ⚠️ 세트 사이의 중복을 지우지 않는다 — 세트마다 따로 모으므로 칸도 따로 먹는다.
 */
export function countTargetKeywordUsage(campaigns: Record<string, any>[] | null | undefined): number {
  return (Array.isArray(campaigns) ? campaigns : []).reduce((sum, campaign) => {
    const keywords = (Array.isArray(campaign?.keywords) ? campaign.keywords : []).map(text).filter(Boolean).length;
    const accounts = getCampaignAccounts(campaign, { preferTopLevel: true })
      .filter((a) => text(a?.account ?? a?.username) !== '').length;
    return sum + keywords + accounts;
  }, 0);
}

/**
 * 시딩 계정 칸의 쓴 수 — **진행 중인** 시딩 캠페인들의 계정 합(끝난 캠페인은 칸을 안 먹는다).
 * @param now 기준 시각 — 날짜 경계는 KST 달력이다
 */
export function countSeedingAccountUsage(campaigns: Record<string, any>[] | null | undefined, now: Date): number {
  return filterInProgressCampaigns(campaigns, now).reduce(
    (sum, campaign) => sum + getCampaignAccounts(campaign).filter((a) => text(a?.account ?? a?.username) !== '').length,
    0,
  );
}

/**
 * 시딩 계정 칸의 한도 — 그 워크스페이스 플랜 표의 `campaign.campaignAccountCount`(만료면 무료 표 · 시딩이 없으면 0).
 * 🔑 맞춤 한도를 받는 플랜(`acceptsCustomPlanService`)은 문서의 `planService.seeding.maxLimit` 이 있으면 그것이 이긴다.
 * ⚠️ 체험·개인·그로스는 문서 값을 읽지 않는다 — 문서에 남은 값이 플랜 표를 덮으면 칸이 플랜과 어긋난다.
 * @returns 문서가 없으면(불러오는 중) `SEEDING_ACCOUNT_DEFAULT_LIMIT`
 */
export function toSeedingAccountLimit(workspace: Record<string, any> | null | undefined): number {
  if (!workspace) return SEEDING_ACCOUNT_DEFAULT_LIMIT;
  const subscriptionPlan = workspace.subscriptionPlan;
  const custom = Number(subscriptionPlan?.planService?.seeding?.maxLimit ?? NaN);
  const ctx = getWorkspacePlanAccessContext(workspace);
  if (!ctx.isExpired && ctx.planId && acceptsCustomPlanService(ctx.planId) && Number.isFinite(custom)) return custom;
  const campaign = ctx.config?.campaign;
  if (campaign?.campaignYN !== true) return 0;
  const limit = Number(campaign.campaignAccountCount);
  return Number.isFinite(limit) ? limit : 0;
}

/** 쓴 수·한도·막힘 → 칸 하나 */
export function toTrackCapacity(area: TrackArea, used: number, limit: number | null, locked: boolean): TrackCapacity {
  const full = !locked && limit !== null && limit > 0 && used >= limit;
  return {
    area,
    name: TRACK_AREA_NAME[area],
    used,
    limit,
    locked,
    full,
    color: full ? 'warning.main' : 'text.secondary',
  };
}

/**
 * 추적 세 갈래의 칸 — 플랜 표에서 한도와 막힘을 읽는다.
 * @param config `usePlanAccess().config`
 */
export function toPlanTrackCapacity(
  area: Exclude<TrackArea, 'seedingAccount'>,
  config: PlanConfig | null | undefined,
  used: number,
): TrackCapacity {
  return toTrackCapacity(area, used, toPlanTrackLimit(config, area), isPlanTrackAreaLocked(config, area));
}

/**
 * 관리자 맞춤 한도 가운데 «사실상 제한 없음»으로 둔 자리 — 공개 플랜의 칸은 300 을 넘지 않는다(엔터프라이즈 자사 계정).
 * 시딩 계정의 기본값 1,000 도 여기 든다.
 */
export const TRACK_LIMIT_ROOMY_FROM = 999;

/**
 * 한도가 넉넉해 적을 뜻이 없는가 — 「100 / 999」의 999 는 고르는 데 쓸모가 없다(시작하기 「남은 칸 N개」를 안 적는 것과 같은 기준).
 * ⚠️ 다 찼는지(`full`)는 이 판정과 무관하게 실제 한도로 잰다.
 */
export function isRoomyTrackLimit(limit: number | null): boolean {
  return limit !== null && limit >= TRACK_LIMIT_ROOMY_FROM;
}

/** 수 한 토막 — 「3 / 10」 · 한도를 모르면 「3」 */
export function toTrackCapacityText(capacity: Pick<TrackCapacity, 'used' | 'limit'>): string {
  return capacity.limit === null ? fNumber(capacity.used) : `${fNumber(capacity.used)} / ${fNumber(capacity.limit)}`;
}
